import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEBINAR_VIDEO_URL = 'https://customer-assets.emergentagent.com/job_algo-elite/artifacts/rkv46rut_Quant%20Finance%20Expert%20Reveals%20How%20He%20Leverages%20%20His%20A.I%20Software%20to%20Raise%20Capital%C3%B0___%C3%B0__%C2%BF%C3%B0___-3.mp4';

const takeaways = [
  'Direct access to a Quant Portfolio Specialist with the tools and systems to create financial independence for qualified individuals',
  'How to access $100K-$10M in proprietary firm capital without credit checks or traditional financing requirements',
  'The case for algorithmic trading: 24/7 automated execution eliminating emotional decision-making',
  'Why traditional portfolio advisors underperform (10-12% returns) while inflation erodes wealth at 24% over 4 years',
  'The exact system architecture Adam uses to manage $10M+ in fully automated portfolios',
];

export const WebinarSection = () => {
  const [step, setStep] = useState('locked');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const maxTimeRef = React.useRef(0);
  const milestonesReachedRef = React.useRef(new Set());
  const [rewindAllowed, setRewindAllowed] = useState(false);

  // When video unlocks, check if this IP is whitelisted for rewind
  React.useEffect(() => {
    if (step === 'unlocked') {
      axios.get(`${API}/webinar/rewind-status`)
        .then(res => setRewindAllowed(res.data.allowed))
        .catch(() => setRewindAllowed(false));
    }
  }, [step]);

  const validate = () => {
    const errs = {};

    const nameParts = name.trim().split(' ').filter(p => p.length > 0);
    if (!name.trim()) {
      errs.name = 'Name is required';
    } else if (nameParts.length < 2) {
      errs.name = 'Please enter your full name (first and last)';
    } else if (name.length < 3) {
      errs.name = 'Name is too short';
    } else if (!/^[a-zA-Z\s'-]+$/.test(name)) {
      errs.name = 'Please enter a valid name (letters only)';
    }

    const disposableDomains = ['tempmail', 'throwaway', '10minutemail', 'guerrillamail', 'mailinator', 'trashmail'];
    if (!email.trim()) {
      errs.email = 'Email is required';
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
      errs.email = 'Please enter a valid email address';
    } else if (disposableDomains.some(domain => email.toLowerCase().includes(domain))) {
      errs.email = 'Please use a permanent email address';
    }

    const phoneDigits = phone.replace(/\D/g, '');
    if (!phone.trim()) {
      errs.phone = 'Phone is required';
    } else if (phoneDigits.length < 10) {
      errs.phone = 'Please enter a valid phone number';
    } else if (!/^[\d\s\-\(\)\+]+$/.test(phone)) {
      errs.phone = 'Please enter a valid phone number';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleUnlock = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        source: 'webinar_private',
      });
      setStep('nda');
      setDialogOpen(false);
      toast.success('Information submitted.');
    } catch {
      toast.error('Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  // FIX: Single function — previously had a duplicate inner definition that prevented setStep from being called
  const handleNdaAccept = () => {
    if (!ndaAccepted) {
      toast.error('You must accept the NDA to proceed. Please read the terms and check the box above.', {
        duration: 5000,
      });
      return;
    }
    setStep('unlocked');
    toast.success('Access granted.');
  };

  const handleVideoProgress = (e) => {
    const video = e.target;
    // Track max time reached — prevents rewind past this point
    if (video.currentTime > maxTimeRef.current) {
      maxTimeRef.current = video.currentTime;
    }

    const progress = (video.duration > 0) ? (video.currentTime / video.duration) * 100 : 0;
    localStorage.setItem('webinar_progress', progress.toString());

    // Send milestone progress to analytics (25, 50, 75, 90) — once per session
    for (const milestone of [25, 50, 75, 90]) {
      if (progress >= milestone && !milestonesReachedRef.current.has(milestone)) {
        milestonesReachedRef.current.add(milestone);
        axios.post(`${API}/track/video-progress`, {
          milestone,
          name: name,
          email: email,
        }).catch(() => {});
      }
    }

    if (progress >= 90 && !localStorage.getItem('webinar_90_reached')) {
      localStorage.setItem('webinar_90_reached', 'true');
      window.dispatchEvent(new Event('webinar-90-percent'));
      toast.success('Next Steps Unlocked! Scroll below to schedule your call with Adam.', {
        duration: 6000,
      });
    }
  };

  const handleVideoSeeking = (e) => {
    // If rewind is not approved for this IP, block going back more than 5 seconds
    if (!rewindAllowed && e.target.currentTime < maxTimeRef.current - 5) {
      e.target.currentTime = maxTimeRef.current;
    }
  };

  return (
    <section
      id="webinar"
      data-testid="webinar-section"
      className="relative py-24 sm:py-32"
      style={{ backgroundColor: '#000000' }}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div
            className="inline-flex items-center gap-2 mb-6"
            style={{ fontFamily: 'DM Sans', color: '#6B7280', letterSpacing: '2px', textTransform: 'uppercase', fontSize: '11px', fontWeight: 500 }}
          >
            Exclusive Private Webinar
          </div>
          <h2
            className="text-5xl sm:text-6xl font-bold mb-6"
            style={{ fontFamily: 'Cormorant Garamond', color: '#FFFFFF', letterSpacing: '-0.02em' }}
          >
            Exclusive Private Webinar
          </h2>
          <p className="text-lg max-w-2xl leading-relaxed mb-3" style={{ color: '#9CA3AF', fontFamily: 'DM Sans' }}>
            This webinar is not publicly available. Access granted by personal invitation only.
          </p>
          <div className="max-w-2xl leading-relaxed space-y-4" style={{ fontFamily: 'DM Sans' }}>
            <p>
              <span
                className="text-2xl sm:text-3xl font-bold block mb-3"
                style={{
                  background: 'linear-gradient(90deg, #2563EB 0%, #60A5FA 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Watch the full recorded webinar
              </span>
            </p>
            <p className="text-base" style={{ color: '#9CA3AF' }}>
              — and <strong style={{ color: '#FFFFFF' }}>don't waste your own time</strong> by not paying attention.
            </p>
            <p className="text-base leading-relaxed" style={{ color: '#D1D5DB' }}>
              <span
                style={{
                  background: 'linear-gradient(90deg, #60A5FA, #FFFFFF)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontWeight: 600,
                  fontSize: '1.05rem',
                }}
              >
                You owe it to yourself
              </span>{' '}
              to take note of how the information in this recording applies to where you are — or where you want to be.
            </p>
            <p className="text-base leading-relaxed" style={{ color: '#9CA3AF' }}>
              Ask yourself honestly: is there{' '}
              <strong style={{ color: '#FFFFFF' }}>anyone</strong>{' '}
              besides{' '}
              <span
                style={{
                  background: 'linear-gradient(90deg, #2563EB, #60A5FA)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontWeight: 700,
                }}
              >
                TrepTradingTechnologies
              </span>
              {' '}— or who you may know as Adam — that is within your reach to give you access to the information and tools you will witness here today?
            </p>
            <div
              className="border-l-2 pl-4 py-1 mt-4"
              style={{ borderColor: '#2563EB' }}
            >
              <p className="text-sm font-semibold" style={{ color: '#FFFFFF', letterSpacing: '0.02em' }}>
                There will be{' '}
                <span
                  style={{
                    background: 'linear-gradient(90deg, #2563EB, #60A5FA)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  no rewinds
                </span>
                {' '}or reloading the page.{' '}
                <span style={{ color: '#FFFFFF' }}>This is your shot</span> — bet on yourself by paying attention.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          <div
            className="overflow-hidden"
            style={{
              backgroundColor: '#0a0a0a',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            {step === 'locked' && (
              <div data-testid="webinar-locked-state">
                <div style={{ position: 'relative', paddingTop: '56.25%', backgroundColor: '#000000' }}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 flex items-center justify-center" style={{ border: '2px solid #1E40AF' }}>
                      <Lock size={32} style={{ color: '#1E40AF' }} />
                    </div>
                  </div>
                </div>

                <div className="p-10">
                  <Button
                    data-testid="webinar-unlock-open-button"
                    onClick={() => setDialogOpen(true)}
                    className="w-full h-14 text-base font-semibold inline-flex items-center justify-center gap-2 mb-10"
                    style={{
                      background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 60%, #1e3a8a 100%)',
                      color: '#FFFFFF',
                      fontFamily: 'DM Sans',
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                      border: 'none',
                      boxShadow: '0 8px 32px rgba(30, 64, 175, 0.45)',
                    }}
                  >
                    <Play size={18} />
                    REQUEST PRIVATE ACCESS
                  </Button>

                  <h3 className="text-xl font-bold mb-8" style={{ fontFamily: 'Cormorant Garamond', color: '#FFFFFF' }}>
                    What You'll Discover:
                  </h3>

                  <div className="space-y-5">
                    {takeaways.map((t, i) => (
                      <div key={i} className="flex items-start gap-4">
                        <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#1E40AF' }} />
                        <span className="text-base leading-relaxed" style={{ color: '#D1D5DB', fontFamily: 'DM Sans' }}>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 'nda' && (
              <div className="p-12" data-testid="webinar-nda-state">
                <h3 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Cormorant Garamond', color: '#FFFFFF' }}>
                  Non-Disclosure Agreement
                </h3>

                <div className="mb-10 p-8" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <p className="text-base leading-relaxed mb-6" style={{ color: '#D1D5DB', fontFamily: 'DM Sans' }}>
                    The information in this webinar is confidential and proprietary.
                  </p>
                  <p className="text-base font-semibold mb-4" style={{ color: '#FFFFFF', fontFamily: 'DM Sans' }}>
                    By checking the box below, you agree to:
                  </p>
                  <ul className="space-y-4 text-base" style={{ color: '#9CA3AF', fontFamily: 'DM Sans' }}>
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 mt-2 bg-[#1E40AF] flex-shrink-0" />
                      Not share, reproduce, or distribute any part of this content without written consent
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 mt-2 bg-[#1E40AF] flex-shrink-0" />
                      Keep all strategies, methods, and results discussed strictly confidential
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 mt-2 bg-[#1E40AF] flex-shrink-0" />
                      Acknowledge that trading involves substantial risk and past results do not guarantee future performance
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 mt-2 bg-[#1E40AF] flex-shrink-0" />
                      Acknowledge that Adam Henry and TrepTradingTechnologies are not registered financial advisors. All content, strategies, and information shared are for educational and informational purposes only and do not constitute financial advice
                    </li>
                  </ul>
                </div>

                <div
                  className="flex items-start gap-4 mb-10 p-6"
                  style={{ border: '2px solid #1E40AF', backgroundColor: 'rgba(30,64,175,0.05)' }}
                >
                  <Checkbox
                    checked={ndaAccepted}
                    onCheckedChange={setNdaAccepted}
                    className="mt-0.5"
                    style={{ width: '24px', height: '24px' }}
                  />
                  <label className="text-base font-medium cursor-pointer" style={{ color: '#FFFFFF', fontFamily: 'DM Sans' }}>
                    <span style={{ color: '#1E40AF' }}>* Required:</span> I have read and agree to the terms above
                  </label>
                </div>

                <Button
                  onClick={handleNdaAccept}
                  className="w-full h-14 text-sm font-semibold"
                  style={{
                    background: ndaAccepted
                      ? 'linear-gradient(135deg, #2563EB 0%, #1E40AF 60%, #1e3a8a 100%)'
                      : 'rgba(255,255,255,0.05)',
                    color: ndaAccepted ? '#FFFFFF' : '#6B7280',
                    fontFamily: 'DM Sans',
                    letterSpacing: '0.5px',
                    border: 'none',
                    boxShadow: ndaAccepted ? '0 8px 32px rgba(30, 64, 175, 0.45)' : 'none',
                  }}
                >
                  {ndaAccepted ? 'ACCEPT & WATCH WEBINAR' : 'PLEASE ACCEPT TERMS ABOVE'}
                </Button>
              </div>
            )}

            {step === 'unlocked' && (
              <div data-testid="webinar-video-embed" style={{ position: 'relative', paddingTop: '56.25%', backgroundColor: '#000' }}>
                <video
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                  controls
                  controlsList="nodownload"
                  onTimeUpdate={handleVideoProgress}
                  onSeeking={handleVideoSeeking}
                  preload="metadata"
                >
                  <source src={WEBINAR_VIDEO_URL} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="webinar-unlock-dialog"
          className="sm:max-w-md border p-8"
          style={{ backgroundColor: '#0a0a0a', borderColor: 'rgba(255,255,255,0.15)' }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond', color: '#FFFFFF' }}>
              Request Access
            </DialogTitle>
            <p className="text-sm" style={{ color: '#9CA3AF', fontFamily: 'DM Sans' }}>
              Provide your details to access the private webinar.
            </p>
          </DialogHeader>

          <div className="space-y-5 mt-6">
            <div>
              <Label className="text-xs mb-2 block" style={{ color: '#D1D5DB', fontFamily: 'DM Sans' }}>
                <span style={{ color: '#1E40AF' }}>* </span>Full Name (First &amp; Last)
              </Label>
              <Input
                data-testid="webinar-unlock-name-input"
                value={name}
                onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: '' })); }}
                placeholder="John Carter"
                className="h-12"
                style={{ backgroundColor: '#000000', borderColor: errors.name ? '#1E40AF' : 'rgba(255,255,255,0.15)', color: '#FFFFFF' }}
              />
              {errors.name && <p className="text-xs mt-1" style={{ color: '#1E40AF' }}>{errors.name}</p>}
            </div>
            <div>
              <Label className="text-xs mb-2 block" style={{ color: '#D1D5DB', fontFamily: 'DM Sans' }}>
                <span style={{ color: '#1E40AF' }}>* </span>Email Address (Permanent)
              </Label>
              <Input
                data-testid="webinar-unlock-email-input"
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setErrors(p => ({ ...p, email: '' })); }}
                placeholder="you@email.com"
                className="h-12"
                style={{ backgroundColor: '#000000', borderColor: errors.email ? '#1E40AF' : 'rgba(255,255,255,0.15)', color: '#FFFFFF' }}
              />
              {errors.email && <p className="text-xs mt-1" style={{ color: '#1E40AF' }}>{errors.email}</p>}
            </div>
            <div>
              <Label className="text-xs mb-2 block" style={{ color: '#D1D5DB', fontFamily: 'DM Sans' }}>
                <span style={{ color: '#1E40AF' }}>* </span>Phone Number
              </Label>
              <Input
                data-testid="webinar-unlock-phone-input"
                type="tel"
                value={phone}
                onChange={e => { setPhone(e.target.value); setErrors(p => ({ ...p, phone: '' })); }}
                placeholder="+1 (555) 000-0000"
                className="h-12"
                style={{ backgroundColor: '#000000', borderColor: errors.phone ? '#1E40AF' : 'rgba(255,255,255,0.15)', color: '#FFFFFF' }}
              />
              {errors.phone && <p className="text-xs mt-1" style={{ color: '#1E40AF' }}>{errors.phone}</p>}
              <p className="text-xs mt-1" style={{ color: '#6B7280', fontFamily: 'DM Sans' }}>Include country code for international numbers</p>
            </div>
            <Button
              data-testid="webinar-unlock-submit-button"
              onClick={handleUnlock}
              disabled={loading}
              className="w-full h-12 text-base font-semibold"
              style={{
                backgroundColor: '#1E40AF',
                color: '#FFFFFF',
                fontFamily: 'DM Sans',
                fontWeight: 600,
                letterSpacing: '0.5px',
                border: 'none',
              }}
            >
              {loading ? 'PROCESSING...' : 'REQUEST ACCESS'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
